// jobAccess.js — single source of truth for who can see/edit what in the Jobs app.
//
// ALLOWLIST model: every capability is granted explicitly, never denied. This is
// deliberate — the old code used `!isCrew` (a blocklist), which silently leaked
// sensitive surfaces to the `viewer` role and to any blank/unknown role. Always
// gate on a named capability below, never on `role !== "crew"`.
//
// Unknown / blank / unexpected roles normalize to `viewer` (least privilege)
// BEFORE any capability is read.
//
// NOTE: this is UI gating only. The role comes from `users/{uid}.role` (the
// frontend gate). It is not backend/rules enforcement.

const ADMIN_ROLES = new Set(["admin", "superadmin"]);

// Admin tab keys, in display order (each must exist in JobDetailStub's TABS_ALL).
const ALL_TABS = ["jobCard", "delivReq", "preProd", "capture", "processing", "delivery", "docsFiles", "financials"];
// Crew + viewer see the front of the job, plus "files" — the read-only list of
// files an admin marked crew-visible on Docs & Files (legacy parity; admins
// manage files there, so "files" is deliberately NOT in ALL_TABS).
const LIMITED_TABS = ["jobCard", "preProd", "capture", "files"];

export function jobAccess(rawRole) {
  const role = ADMIN_ROLES.has(rawRole)
    ? rawRole
    : rawRole === "crew"
      ? "crew"
      : "viewer"; // viewer + unknown + blank all collapse to least privilege

  const isAdmin = ADMIN_ROLES.has(role);
  const isCrew = role === "crew";
  const isViewer = role === "viewer";

  return {
    role,
    isAdmin,
    isCrew,
    isViewer,

    // What you can SEE
    canSeeMoney: isAdmin,        // revenue, costs, Invoiced/Paid, Financials tab
    canSeeContacts: isAdmin,     // contact name + Linked Contacts
    canSeeNotes: isAdmin,        // the internal Notes section on the job card
    canSeeInternalMeta: isAdmin, // Internal Metadata (client code, project, industry, quote date)
    canUseQuotes: isAdmin,       // the Quotes screen + quote version history

    // What you can DO
    canEditClient: isAdmin,           // the Job Card identity fields
    canEditJobCard: isAdmin,          // the job card itself: edit toggle + identity/schedule/location/metadata fields
    canEditShootWork: isAdmin || isCrew, // Prep + Capture
    canChangeStatus: isAdmin,         // advancing the job status
    canExportJob: isAdmin,            // Export JSON (dumps the whole job)
    canManageJobs: isAdmin,           // restore / delete / permanent-delete / unbook / unlink / session edits

    // Which detail tabs are reachable
    visibleTabs: isAdmin ? ALL_TABS : LIMITED_TABS,
  };
}

// Top-level Jobs app nav tabs that require a capability to be visible.
// Keyed by the display label in appRegistry.js navItems (which is also what
// JobsApp routes on). Anything not listed here is always shown.
const NAV_TAB_CAPABILITY = { Quotes: "canUseQuotes", Insights: "canSeeMoney" };

// Filter the Jobs app's top-nav labels down to what this role may see.
// Returns a NEW array, order preserved. The SAME filtered array must feed both
// the topbar (rendering) and JobsApp (view routing) — if they ever used two
// separate lists, hiding a tab would shift positions and misroute the
// survivors (e.g. "Jobs" opening the Quotes/pricing screen).
export function visibleJobsNav(navItems, rawRole) {
  const access = jobAccess(rawRole);
  return (navItems || []).filter((label) => {
    const cap = NAV_TAB_CAPABILITY[label];
    return !cap || access[cap];
  });
}
